import { useCallback, useEffect, useRef, useState } from 'react';

// Distance (px) from the newest message at which the scroll-to-latest button reaches
// full presence — the button scales/fades from 0 (within the at-bottom threshold) to 1.
// Mirrors the native apps' 220dp/pt full-distance (see docs/chat-arrow-down-behavior-handoff.md).
const AT_BOTTOM_THRESHOLD = 40;
const SCROLL_BUTTON_FULL_DISTANCE = 220;

// When opening the brief at its summary, leave a sliver of the thread above the
// summary visible so it reads as "scrolled into" the meeting, not the raw top of a
// card. Mirrors native's `briefSummaryPeek` / `BRIEF_SUMMARY_PEEK` (56).
const OPEN_AT_MESSAGE_PEEK = 56;

// Open-at-a-message highlight timing. Shared 1:1 with iOS/Android; the CSS animation in globals.css
// uses the same 1.8s hold + 0.8s fade.
const MESSAGE_HIGHLIGHT_HOLD_MS = 1800;
const MESSAGE_HIGHLIGHT_FADE_MS = 800;
// After landing on an open-at-message target, keep it pinned while the content above it settles (images,
// markdown tables still laying out), and let go the moment the reader scrolls. Mirrors native's ~1.8s settle.
const OPEN_TARGET_SETTLE_MS = 2150;

// Breathing room above the pinned send, matching native's `ChatScrollAnchor.anchorTopInset`.
const ANCHOR_TOP_INSET = 16;

/** The reserve this hook is currently rendering, read off the DOM so it never disagrees with it. */
function reserveHeight(el: HTMLElement): number {
  return (el.querySelector('[data-send-spacer]') as HTMLElement | null)?.offsetHeight ?? 0;
}

/**
 * The browser's own scroll anchoring, off while a send is held. It keeps whatever row it picked still when
 * content above that row changes, and during a reply it picks the loader: a picture placeholder inserted
 * above "Thinking" then pushed the just-sent message, and the game in the placeholder, off the top. The
 * reserve already holds the pin without moving anything, so the two only fight.
 */
function setBrowserAnchoring(el: HTMLElement | null, on: boolean): void {
  if (el) el.style.overflowAnchor = on ? '' : 'none';
}

function messageElement(el: HTMLElement | null, id: string | null): HTMLElement | null {
  if (!el || !id) return null;
  return Array.from(el.querySelectorAll<HTMLElement>('[data-msg-id]')).find(row => row.dataset.msgId === id) ?? null;
}

// Owns the message-pane scroll behavior: auto-stick to bottom, the
// scroll-to-latest affordance, and the send-to-top glide (the just-sent user
// bubble rises to the top so the reply streams in beneath it, ChatGPT-style).
export function useScroll({
  conversationId,
  lastUserMsgId,
  turnInFlight,
  scrollToMessageId,
  highlightOnOpen = false,
  openTargetUnreachable = false,
}: {
  conversationId: string | null;
  lastUserMsgId: string | null;
  turnInFlight: boolean;
  // When set, open the thread at the TOP of this message instead of the tail —
  // the home Morning Brief peek lands on the start of the round-table's wrap-up
  // summary rather than the newest turn. Null for every ordinary conversation.
  scrollToMessageId?: string | null;
  // True only when the target came from a notification / search deep link (not the brief peek):
  // the landed-on row is briefly highlighted. Fires once per open-at-target.
  highlightOnOpen?: boolean;
  // The open-at-message search gave up (`lib/open-target-search.ts`): the target will never be drawn.
  // Stop waiting for it and settle at the bottom like an ordinary open — no highlight.
  openTargetUnreachable?: boolean;
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const isAtBottomRef = useRef(true);
  const jumpingRef = useRef(false);
  const pendingScrollTopRef = useRef(false);
  // The open-at-a-message target, read synchronously by the open effect's observer
  // (history loads async, so the row may not exist yet when the thread opens).
  const scrollToMsgIdRef = useRef<string | null>(null);
  scrollToMsgIdRef.current = scrollToMessageId ?? null;
  const pendingOpenScrollRef = useRef(false);
  // Until when (performance.now) a content resize re-pins the open target; 0 = not settling.
  const openSettleUntilRef = useRef(0);
  const highlightOnOpenRef = useRef(false);
  highlightOnOpenRef.current = highlightOnOpen;
  // The just-sent message held near the top while its reply fills the space below it, and whether the
  // caller has since taken the scroll back. Ports native's `ChatScrollAnchor` (`pinnedMessageId` /
  // `pinFrozenByUser`); see `recomputeReserve` for why this removes the lurch rather than hiding it.
  const pinnedIdRef = useRef<string | null>(null);
  const pinReleasedRef = useRef(false);
  // The highlighted row + its removal timer. Imperative (a data attribute the CSS animates) so no
  // React state changes and the list never re-renders; cleared on unmount / conversation switch.
  const highlightRef = useRef<{ row: HTMLElement; timer: ReturnType<typeof setTimeout> } | null>(null);
  const clearHighlight = () => {
    const h = highlightRef.current;
    if (!h) return;
    clearTimeout(h.timer);
    h.row.removeAttribute('data-msg-highlight');
    highlightRef.current = null;
  };
  const highlightRow = (row: HTMLElement) => {
    clearHighlight();
    row.setAttribute('data-msg-highlight', '');
    const reduced = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const total = reduced ? MESSAGE_HIGHLIGHT_HOLD_MS : MESSAGE_HIGHLIGHT_HOLD_MS + MESSAGE_HIGHLIGHT_FADE_MS;
    const timer = setTimeout(clearHighlight, total);
    highlightRef.current = { row, timer };
  };
  const [scrolledUp, setScrolledUp] = useState(false);
  // 0…1, driven by distance from the newest message. The button scales/fades by it; it is
  // held at 0 while auto-following (jumping) or within the at-bottom threshold, so it only
  // ever rises when the user is manually away from the tail — matching native's
  // `showScrollToBottom ? progress : 0`.
  const [scrollProgress, setScrollProgress] = useState(0);
  const [sendSpacer, setSendSpacer] = useState(0);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = distance < AT_BOTTOM_THRESHOLD;
    isAtBottomRef.current = atBottom;
    if (atBottom) jumpingRef.current = false;
    const shown = !atBottom && !jumpingRef.current;
    setScrolledUp(shown);
    setScrollProgress(
      shown
        ? Math.min(
            Math.max((distance - AT_BOTTOM_THRESHOLD) / (SCROLL_BUTTON_FULL_DISTANCE - AT_BOTTOM_THRESHOLD), 0),
            1,
          )
        : 0,
    );
    // Scrolling is when an unseen reserve becomes free to drop.
    releaseReserveIfUnseen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Size the tail reserve to exactly the part of the viewport the reply has NOT filled yet, so that
   * `anchor + reply + reserve` always adds up to one viewport.
   *
   * This is native's `ChatScrollAnchor.recomputePinSpacer` (`viewport − anchorHeight − replyFootprint`),
   * and the arithmetic is the whole point: the reply grows by exactly what the reserve gives up, the
   * scrollable height never changes, and so the pinned message does not move. No scroll position has to
   * be re-asserted while the reply streams, which is what used to fight the glide.
   *
   * A flat viewport-sized reserve — what this did before — empties the thread on screen and then has to
   * be taken away in one step when the turn settles, which is where the ~390px lurch came from.
   */
  const recomputeReserve = useCallback(() => {
    const el = scrollRef.current;
    const id = pinnedIdRef.current;
    if (!el || !id) return;
    const msgEl = messageElement(el, id);
    if (!msgEl) return;
    const belowAnchor = el.scrollHeight - reserveHeight(el) - (msgEl.offsetTop + msgEl.offsetHeight);
    const target = el.clientHeight - ANCHOR_TOP_INSET - msgEl.offsetHeight - Math.max(0, belowAnchor);
    setSendSpacer(Math.max(0, target));
  }, []);

  /**
   * Let the reserve go, but only while it sits entirely below the fold.
   *
   * Shortening the content shortens the scrollable range, and the browser then clamps `scrollTop` — that
   * clamp IS the lurch. Below the fold there is nothing to clamp, so it costs the caller nothing; above
   * it, the reserve simply stays until they scroll and it becomes free to drop.
   */
  const releaseReserveIfUnseen = useCallback(() => {
    const el = scrollRef.current;
    if (!el || pinnedIdRef.current) return;
    const reserve = reserveHeight(el);
    if (reserve > 0 && el.scrollTop + el.clientHeight <= el.scrollHeight - reserve) setSendSpacer(0);
  }, []);

  const scrollToLatest = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    jumpingRef.current = true;
    setScrolledUp(false);
    setScrollProgress(0);
    isAtBottomRef.current = true;
    // Asking for the newest message is asking to stop being held at the send.
    pinReleasedRef.current = true;
    // Land on the newest MESSAGE. The raw bottom is the bottom of the reserve, so while one is held this
    // would scroll past the answer into the empty room kept below it.
    const bottom = Math.max(0, el.scrollHeight - reserveHeight(el) - el.clientHeight);
    el.scrollTo({ top: bottom, behavior: 'smooth' });
  }, []);

  // Watch the content for height changes. ResizeObserver fires for new bubbles
  // AND per-bubble typewriter growth (which doesn't bump messages identity).
  useEffect(() => {
    if (!conversationId) return;
    // A brief peek opens at the summary's top; every other thread opens at the tail.
    // `isAtBottom` is what keeps the observer following new content, so a summary
    // open must clear it — otherwise the re-pin below yanks the view back down.
    const openAtMessage = scrollToMsgIdRef.current;
    isAtBottomRef.current = !openAtMessage;
    jumpingRef.current = false;
    pendingScrollTopRef.current = false;
    pendingOpenScrollRef.current = !!openAtMessage;
    openSettleUntilRef.current = 0;
    pinnedIdRef.current = null;
    pinReleasedRef.current = false;
    setScrolledUp(false);
    setScrollProgress(0);
    setSendSpacer(0);
    setBrowserAnchoring(scrollRef.current, true);
    const content = contentRef.current;
    const el = scrollRef.current;
    if (!content || !el) return;
    const scrollToOpenTarget = () => {
      const id = scrollToMsgIdRef.current;
      const msgEl = messageElement(el, id);
      if (!msgEl) return false;
      const firstLanding = pendingOpenScrollRef.current;
      pendingOpenScrollRef.current = false;
      el.scrollTop = Math.max(0, msgEl.offsetTop - OPEN_AT_MESSAGE_PEEK);
      if (firstLanding) {
        openSettleUntilRef.current = performance.now() + OPEN_TARGET_SETTLE_MS;
        if (highlightOnOpenRef.current) highlightRow(msgEl);
      }
      return true;
    };
    if (openAtMessage) scrollToOpenTarget();
    else el.scrollTop = el.scrollHeight;
    const observer = new ResizeObserver(() => {
      // The summary row may not have loaded when the thread first opened — retry on
      // each content resize until it's there, then stop (never fall back to the tail).
      if (pendingOpenScrollRef.current) {
        scrollToOpenTarget();
        return;
      }
      // Landed, but rows above it are still growing: hold the target where it landed.
      if (openSettleUntilRef.current > performance.now()) {
        scrollToOpenTarget();
        return;
      }
      // While a send is pinned, the reply is fed by the reserve rather than by scrolling: recomputing it
      // keeps the total height constant, which holds the pin without writing a scroll position at all.
      // Native calls this "no tailing down during the response" — the caller reads the answer where they
      // sent from, and the scroll-to-latest button is what offers the jump once the reply outgrows the view.
      if (pinnedIdRef.current && !pinReleasedRef.current) {
        recomputeReserve();
        return;
      }
      if (isAtBottomRef.current) el.scrollTop = el.scrollHeight;
    });
    observer.observe(content);
    return () => {
      observer.disconnect();
      clearHighlight();
    };
  }, [conversationId, scrollToMessageId]);

  // Declared AFTER the open effect so that, in a commit that both opens and gives up, it runs second.
  useEffect(() => {
    if (!openTargetUnreachable || !pendingOpenScrollRef.current) return;
    pendingOpenScrollRef.current = false;
    openSettleUntilRef.current = 0;
    isAtBottomRef.current = true;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [openTargetUnreachable, conversationId, scrollToMessageId]);

  // After a send, glide the optimistic user bubble up to the top so the reply
  // streams in beneath it. The tail spacer set by beginSendScroll guarantees the
  // room; isAtBottom stays false so the observer won't yank the view back down.
  useEffect(() => {
    // The server re-keys the optimistic bubble once it confirms it. The pin follows the message, or the
    // reserve stops being measured against anything while the reply is still arriving.
    if (!pendingScrollTopRef.current && pinnedIdRef.current && lastUserMsgId) {
      pinnedIdRef.current = lastUserMsgId;
      return;
    }
    if (!pendingScrollTopRef.current || !lastUserMsgId) return;
    const el = scrollRef.current;
    const msgEl = messageElement(el, lastUserMsgId);
    if (!el || !msgEl) return;
    pendingScrollTopRef.current = false;
    pinnedIdRef.current = lastUserMsgId;
    pinReleasedRef.current = false;
    // Replace the opening viewport-sized reserve with the measured one before the glide runs, so the
    // thread does not read as emptied while the reply is still on its way.
    recomputeReserve();
    el.scrollTo({ top: Math.max(0, msgEl.offsetTop - ANCHOR_TOP_INSET), behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastUserMsgId]);

  // Drop the tail spacer on the in-flight → settled EDGE, not on any falsy
  // moment: right after send there's a window before the stream opens where the
  // turn reads as not-in-flight, and clearing then would yank the spacer out
  // before the glide even runs.
  const prevInFlightRef = useRef(false);
  useEffect(() => {
    if (prevInFlightRef.current && !turnInFlight) {
      // The turn is done, so the pin has nothing left to hold — but the reserve is NOT taken back here.
      // Removing it while the caller is looking at it is what produced the lurch; it goes only once it is
      // out of sight (see releaseReserveIfUnseen), and until then it is just empty space below the answer.
      pinnedIdRef.current = null;
      // Re-read "at bottom" from where the pane really is. It was last written by the glide, which lands
      // flush with the reserve's bottom, and a reply growing past the fold fires no scroll event — so it
      // still reads true. Left stale, the next late resize (a card laying out, the server re-keying the
      // rows) tails to the end and leaves the start of the answer, the card, above the fold.
      const el = scrollRef.current;
      if (el) isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < AT_BOTTOM_THRESHOLD;
      setBrowserAnchoring(scrollRef.current, true);
      releaseReserveIfUnseen();
    }
    prevInFlightRef.current = turnInFlight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turnInFlight]);

  const beginSendScroll = useCallback(() => {
    setBrowserAnchoring(scrollRef.current, false);
    isAtBottomRef.current = false;
    pendingScrollTopRef.current = true;
    pinReleasedRef.current = false;
    // An opening reserve, so the row has somewhere to rise into on the very first frame. The glide effect
    // measures it properly as soon as the row exists.
    setSendSpacer(scrollRef.current?.clientHeight ?? 0);
  }, []);

  /**
   * The caller has taken the scroll back, so stop holding their send at the top.
   *
   * Wired to wheel/touch-drag rather than to `scroll`, because a scroll event cannot say who caused it —
   * and the reserve recompute causes plenty of them. Native draws the same line (`pinFrozenByUser`).
   */
  const releasePin = useCallback(() => {
    pinReleasedRef.current = true;
    openSettleUntilRef.current = 0; // the reader is scrolling: stop holding an open target
  }, []);

  /**
   * Ends any scroll animation still in flight, at wherever it has reached.
   *
   * The send glide and the scroll-to-latest jump are both `behavior: 'smooth'`, so the pane keeps
   * moving for a few hundred ms after they start. Press something during that window and it travels
   * out from under the pointer: the browser produces no click, because press and release happened
   * over different elements — and the next press lands on whatever slid into that spot, which for a
   * row of choice chips means answering with a chip the caller never aimed at. Measured on a real
   * thread: a chip pressed at y=254 was at y=802 by release, 548px away, and the neighbouring option
   * was sent instead.
   *
   * Pinning to the live offset is what a native scroll view does when a finger lands on moving
   * content — the animation is a convenience, and the caller reaching for something outranks it.
   */
  const freezeScroll = useCallback(() => {
    openSettleUntilRef.current = 0; // a press is the reader taking over, same as a wheel/touch
    const el = scrollRef.current;
    // `instant` rather than `auto`: auto defers to the element's computed `scroll-behavior`, so a
    // stylesheet setting `smooth` would make the cancellation itself animate and cancel nothing.
    if (el) el.scrollTo({ top: el.scrollTop, behavior: 'instant' });
  }, []);

  return {
    scrollRef,
    contentRef,
    onScroll,
    freezeScroll,
    releasePin,
    scrollToLatest,
    scrolledUp,
    scrollProgress,
    sendSpacer,
    beginSendScroll,
  };
}
